import { principles, founder } from "../data/site";
import { FadeUp } from "../components/motion";
import { SafeImage } from "../components/Media";
import { useLang } from "../i18n";
import { useSEO } from "../lib/seo";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "../components/ui/dialog";
const TEAM_CONTENT_KEY = "json:about_team_members";

const localizeTeamField = (value, lang) => {
  if (value && typeof value === "object") return value[lang] || value.en || "";
  return value || "";
};

const parseTeam = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function About() {
  const { t, ov, lang } = useLang();
  const savedTeam = ov(TEAM_CONTENT_KEY, "");
  const defaultMember = {
    id: "founder",
    name: ov("text:founder_name", t(founder.name)),
    role: ov("text:founder_role", t(founder.role)),
    bio: ov("text:founder_bio_1", t(founder.bio[0])),
    photo: ov("image:founder_photo", founder.photo),
    published: true,
  };
  const team = (savedTeam ? parseTeam(savedTeam) : [defaultMember]).filter((member) => {
    const name = localizeTeamField(member.name, lang).trim();
    const role = localizeTeamField(member.role, lang).trim();
    return member.published !== false && name && role && member.photo && member.photo !== "__NONE__";
  });
  useSEO(
    t("About — YUXEL"),
    t("YUXEL brings together business strategy, brand and marketing, creative direction, sales and analytics.")
  );
  return (
    <div className="about-page">
      <section className="about-intro" aria-labelledby="about-intro-title">
        <div className="about-intro-content">
          <p className="about-intro-label">{lang === "az" ? "YUXEL HAQQINDA" : "ABOUT YUXEL"}</p>
          <h1 id="about-intro-title">{lang === "az" ? "Strategiyadan icraya, vahid komanda ilə." : "From strategy to execution, as one team."}</h1>
          <p className="about-intro-description">
            {lang === "az"
              ? "YUXEL biznes strategiyasını, brend və marketinqi, kreativ istiqaməti, satış və analitikanı bir araya gətirir. İstehsal işləri seçilmiş tərəfdaşlarla, vahid rəhbərlik və keyfiyyət nəzarəti altında həyata keçirilir."
                : "YUXEL brings together business strategy, brand and marketing, creative direction, sales and analytics. Production work is carried out with selected partners under unified leadership and quality control."}
          </p>
        </div>
      </section>

      <section className="about-team" aria-labelledby="about-team-title">
        <div className="about-team-inner">
          <h2 id="about-team-title">{lang === "az" ? "Əməkdaşlarımız" : "Our team"}</h2>
          <p className="about-team-intro">
            {lang === "az"
              ? "Komandamız strategiyanı gündəlik qərarlara və ölçülə bilən nəticələrə çevirən təcrübəni birləşdirir."
              : "Our team brings together the experience to turn strategy into day-to-day decisions and measurable results."}
          </p>
          {team.length > 0 && (
            <div className="about-team-grid" data-testid="about-team-grid">
              {team.map((member) => {
                const name = localizeTeamField(member.name, lang);
                const role = localizeTeamField(member.role, lang);
                const shortBio = localizeTeamField(member.bio, lang);
                const fullBio = localizeTeamField(member.fullBio, lang) || shortBio;
                const email = member.email || member.contactEmail;
                return (
                  <Dialog key={member.id || name}>
                    <DialogTrigger asChild>
                      <button type="button" className="about-team-member" aria-label={`${name} — ${role}`}>
                        <span className="about-team-photo">
                          <SafeImage src={member.photo} alt={name} loading="lazy" />
                        </span>
                        <span className="about-team-name">{name}</span>
                        <span className="about-team-role">{role}</span>
                        {shortBio && <span className="about-team-bio">{shortBio}</span>}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="about-team-dialog" overlayClassName="about-team-dialog-overlay" aria-describedby={`team-bio-${member.id || name}`}>
                      <div className="about-team-dialog-photo">
                        <SafeImage src={member.photo} alt={name} />
                      </div>
                      <div className="about-team-dialog-copy">
                        <p className="about-team-dialog-label">{lang === "az" ? "YUXEL KOMANDASI" : "THE YUXEL TEAM"}</p>
                        <DialogTitle className="about-team-dialog-name">{name}</DialogTitle>
                        <p className="about-team-dialog-role">{role}</p>
                        <DialogDescription id={`team-bio-${member.id || name}`} className="about-team-dialog-bio">
                          {fullBio || (lang === "az" ? "Bioqrafiya tezliklə əlavə ediləcək." : "Biography coming soon.")}
                        </DialogDescription>
                        {(email || member.linkedin) && (
                          <div className="about-team-dialog-links">
                            {email && <a href={`mailto:${email}`}>{email}</a>}
                            {member.linkedin && <a href={member.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="about-principles" aria-label="Principles">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
          <FadeUp>
            <p className="lux-eyebrow">02 — {t("How we work")}</p>
            <h2 className="case-dark-heading mt-6 max-w-4xl">{t("Four principles we don't negotiate.")}</h2>
          </FadeUp>
          <div className="mt-14 border-t border-white/14">
            {principles.map((p, i) => (
              <FadeUp key={p.n} delay={i * 0.05}>
                <div data-testid={`about-principle-${p.n}`} className="about-principle-row">
                  <span>{p.n}</span><h3>{t(p.t)}</h3><p>{t(p.d)}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
